/**
 * Admin › Settings — the top of the page: what the league voted for and cannot yet have, then the
 * deadlines. Pass 15 rev B, 3 September 2026.
 *
 * ONE TABLE, A ROW PER DEADLINE. A commissioner arrives knowing which deadline he came to move,
 * and reading down a column beats hunting through stacked fields for it. The columns are the four
 * questions he has — which · when · what now · locked — and the row states its own live
 * consequence rather than making him work it out.
 *
 * THE LOCK IS A TOGGLE WITH THREE READINGS, and the third is new. Before the deadline, off means
 * the gate follows the date and on means it shut early. After the deadline it reads on — locked by
 * the date — and flipping it off is the FORCE UNLOCK: it always opens the confirmation, states the
 * consequences in the present tense, and leaves the row wearing the exception (amber edge, "Open ·
 * exception", who did it, one-press Re-lock) until somebody re-locks it or moves the date. Never a
 * silent flip. See `deadlines.GateException` for what it writes and why moving a date spends it.
 *
 * THE TOGGLE ACTS AT ONCE; THE DATES WAIT FOR SAVE. A lock is one field and the dangerous version
 * of it has its own confirmation, so it writes immediately and the row reflects the database. A
 * date is typed, echoed, checked against its rule and then committed with the others by the one
 * Save, because half-typed dates saving themselves is how a deadline lands in the wrong month.
 *
 * Managers never reach this page — the section is `min: 'commissioner'` — so the read-only branch
 * of every control is for a commissioner looking at a value the rules will not let him touch.
 */

import { useEffect, useMemo, useState } from 'react';
import { Linkify } from './RuleText.js';
import { managerShort } from '../domain/identity.js';
import { GmBadge } from './GmBadge.js';
import {
  GATE_FIELDS, LEAGUE_ZONE, ZONES, bothZones, clockIn, exceptionStands, inZone, isoFromZoned,
  prefRuleCheck, zonedInputValue, type GateException, type GateKey,
} from '../domain/deadlines.js';
import {
  deadlineRows, type DeadlineFacts, type DeadlineKey, type DeadlineRow,
} from '../domain/deadlineTable.js';
import { LEAGUE_2026 } from '../domain/config.js';
import { atLeast, type LeagueSettings, type LeagueStore, type Role } from '../store/types.js';
import { useLeagueRules } from './useLeagueRules.js';
import { note } from './auditor.js';
import { dock } from './dock.js';

const SEASON = 2026;

/** Which gate a table row is, where it is one. */
const GATE_OF: Partial<Record<DeadlineKey, GateKey>> = {
  keepers: 'keepers', preferences: 'preferences', proposals: 'proposals',
};

/** The one line under each label: what the date actually gates. Rows that gate nothing get none. */
const GATES_WHAT: Partial<Record<DeadlineKey, string>> = {
  keepers: 'gates slate writes',
  preferences: 'gates ranking writes',
  proposals: 'gates docket writes',
};

const LABELS: Record<GateKey, string> = {
  keepers: 'keeper declarations', preferences: 'draft slot preferences', proposals: 'rule proposals',
};

/**
 * What unlocking each gate does, said in the present tense to the person about to do it.
 *
 * Same shape for every gate so the dialog reads the same whichever it is about and the differences
 * are the facts. The last line names him, from the session and never typed.
 */
const UNLOCK_SAYS: Record<GateKey, { title: string; lines: string[] }> = {
  keepers: {
    title: 'Unlock keeper declarations?',
    lines: [
      'All ten GMs can edit and submit slates again, immediately.',
      'Luxury tax already charged stops matching the slates; League Overview will show what to charge again.',
      'The gate stays open until you re-lock it or move the deadline.',
    ],
  },
  preferences: {
    title: 'Unlock draft slot preferences?',
    lines: [
      'All ten GMs can change their rankings again, immediately.',
      'A resolved order is not re-run on its own; a changed ranking needs the lottery re-resolved.',
      'The gate stays open until you re-lock it or move the deadline.',
    ],
  },
  proposals: {
    title: 'Unlock rule proposals?',
    lines: [
      'All ten GMs can submit and edit proposals again, immediately.',
      'The docket reopens — anything the commissioners already ordered can shift.',
      'The gate stays open until you re-lock it or move the deadline.',
    ],
  },
};

export function AdminSettings(
  { gm, role, store }: { gm: string; role: Role; store: LeagueStore },
) {
  const canEdit = atLeast(role, 'commissioner');
  const [settings, setSettings] = useState<LeagueSettings | null>(null);
  const [deadline, setDeadline] = useState('');
  const [prefDeadline, setPrefDeadline] = useState('');
  const [proposalDeadline, setProposalDeadline] = useState('');
  const [committee, setCommittee] = useState('');
  const [committeeLink, setCommitteeLink] = useState('');
  const [draft, setDraft] = useState('');
  const [order, setOrder] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  /** Which zone he is typing in. Eastern by default: the rulebook's "5:00pm" has always meant it. */
  const [zone, setZone] = useState(LEAGUE_ZONE);
  const [facts, setFacts] = useState<DeadlineFacts>({});
  /** The gate whose force-unlock confirmation is open, if any. */
  const [unlocking, setUnlocking] = useState<GateKey | null>(null);
  const { stuck } = useLeagueRules(store);

  useEffect(() => {
    let live = true;
    const load = async () => {
      const [s, ds, decls, prefs, docket] = await Promise.all([
        store.settings(SEASON), store.draftState(SEASON),
        store.declarations(SEASON), store.preferences(SEASON), store.docket(SEASON),
      ]);
      if (!live) return;
      setSettings(s);
      setOrder(ds?.order ?? []);
      setFacts({
        /* A draft slate is not a declaration; counting it says nobody needs chasing on the 31st. */
        declared: { of: LEAGUE_2026.teams, done: decls.filter((d) => d.status === 'submitted').length },
        preferences: { of: LEAGUE_2026.teams, done: prefs.length },
        onDocket: docket?.order?.length ?? 0,
      });
    };
    void load();
    const off = store.subscribe(SEASON, (w) => {
      if (w === 'settings' || w === 'declarations' || w === 'preferences' || w === 'docket') void load();
    });
    return () => { live = false; off(); };
  }, [store]);

  /* Inputs derive from storage plus the zone, so switching zone rewrites the clock, never the instant. */
  useEffect(() => {
    setDeadline(zonedInputValue(settings?.keeperDeadline, zone));
    setPrefDeadline(zonedInputValue(settings?.preferenceDeadline, zone));
    setDraft(zonedInputValue(settings?.draftDate, zone));
    setProposalDeadline(zonedInputValue(settings?.proposalDeadline, zone));
    setCommittee(zonedInputValue(settings?.committeeDate, zone));
    setCommitteeLink(settings?.committeeLink ?? '');
  }, [settings, zone]);

  /** The settings document as the form would write it now. */
  const assemble = (): LeagueSettings => {
    const next: LeagueSettings = {
      ...(settings ?? { declarationsLocked: false }),
      season: SEASON,
      keeperDeadline: isoFromZoned(deadline, zone),
      preferenceDeadline: isoFromZoned(prefDeadline, zone),
      proposalDeadline: isoFromZoned(proposalDeadline, zone),
      committeeDate: isoFromZoned(committee, zone),
      committeeLink: committeeLink.trim() || null,
      draftDate: isoFromZoned(draft, zone),
      declarationsLocked: settings?.declarationsLocked ?? false,
      updatedAt: new Date().toISOString(),
      updatedBy: gm,
    };
    /*
     * MOVING A DATE SPENDS ITS EXCEPTION. `exceptionStands` refuses a stale one anyway, on the
     * client and in the rules; this clears the field so the document does not carry a dead
     * exception around for a year.
     */
    const bag = next as unknown as Record<string, unknown>;
    for (const key of ['keepers', 'preferences', 'proposals'] as GateKey[]) {
      const f = GATE_FIELDS[key];
      const held = bag[f.heldOpen] as GateException | null | undefined;
      if (held && !exceptionStands(held, bag[f.deadline] as string | null)) bag[f.heldOpen] = null;
    }
    return next;
  };

  async function save() {
    const next = assemble();
    await store.saveSettings(next);
    setSettings(next);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  /**
   * The toggle writes at once. Three states, two fields: `locked` for shutting early and
   * `heldOpen` for the exception, never both set, and the exception only ever arrives through the
   * dialog below.
   */
  async function writeGate(patch: Record<string, unknown>) {
    const base = settings ?? assemble();
    const next = {
      ...base, ...patch, updatedAt: new Date().toISOString(), updatedBy: gm,
    } as LeagueSettings;
    await store.saveSettings(next);
    setSettings(next);
  }

  const lockEarly = (key: GateKey, on: boolean) => {
    const f = GATE_FIELDS[key];
    return writeGate({ [f.locked]: on, [f.heldOpen]: null });
  };

  const holdOpen = async (key: GateKey) => {
    const f = GATE_FIELDS[key];
    const over = ((settings as unknown as Record<string, unknown> | null)?.[f.deadline] as string | null | undefined) ?? null;
    const held: GateException = { by: gm.toUpperCase(), at: new Date().toISOString(), over };
    await writeGate({ [f.locked]: false, [f.heldOpen]: held });
    note(store, gm, {
      kind: 'note',
      what: `Held ${LABELS[key]} open after the deadline`,
      target: 'meta/settings',
      detail: {
        Deadline: over ? bothZones(over) : 'not set',
        Reason: 'commissioner unlock for everyone',
      },
    });
  };

  const relock = (key: GateKey) => writeGate({ [GATE_FIELDS[key].heldOpen]: null });

  /* Built from what is IN THE FORM, so a moved date changes the status column under his hands. */
  const rows = useMemo(() => deadlineRows({
    keeperDeadline: isoFromZoned(deadline, zone),
    preferenceDeadline: isoFromZoned(prefDeadline, zone),
    proposalDeadline: isoFromZoned(proposalDeadline, zone),
    committeeDate: isoFromZoned(committee, zone),
    draftDate: isoFromZoned(draft, zone),
    declarationsLocked: settings?.declarationsLocked ?? false,
    declarationsHeldOpen: settings?.declarationsHeldOpen ?? null,
    preferencesLocked: settings?.preferencesLocked ?? false,
    preferencesHeldOpen: settings?.preferencesHeldOpen ?? null,
    proposalsLocked: settings?.proposalsLocked ?? false,
    proposalsHeldOpen: settings?.proposalsHeldOpen ?? null,
  }, facts), [deadline, prefDeadline, proposalDeadline, committee, draft, settings, zone, facts]);

  /*
   * THE ONE DEADLINE A RULE FIXES. 2020-II-1 §5 puts preferences at 5:00pm on the day of the
   * keeper deadline. Still a hand-set field (Tim, 23 August): a note, never a refusal.
   */
  const prefRule = useMemo(
    () => prefRuleCheck(isoFromZoned(deadline, zone), isoFromZoned(prefDeadline, zone)),
    [deadline, prefDeadline, zone],
  );

  const FIELD: Record<DeadlineKey, { value: string; set: (v: string) => void }> = {
    keepers: { value: deadline, set: setDeadline },
    preferences: { value: prefDeadline, set: setPrefDeadline },
    proposals: { value: proposalDeadline, set: setProposalDeadline },
    committee: { value: committee, set: setCommittee },
    draft: { value: draft, set: setDraft },
  };

  const neverSet = Boolean(settings) && !settings?.keeperDeadline && !settings?.preferenceDeadline
    && !settings?.proposalDeadline && !settings?.committeeDate && !settings?.draftDate;

  return (
    <div className="app admin">
      <Linkify>
        {/*
          VOTED IN, NOT YET BUILT — first on the page when there is anything in it, because it is
          the only card here that describes the app being WRONG rather than merely configured.
          Derived from `effect.tasks` on a passed proposal: a rule whose build list has an open
          item. The list is real data, so it is shown, compactly, rather than reduced to a sentence.
        */}
        {stuck.length > 0 && (
          <section className="card voted">
            <div className="votedhead">
              <span className="peyebrow warn">Voted in, not yet built</span>
              <span className="peyebrow warn">
                needed by the {Math.min(...stuck.map((s) => s.by))} draft
              </span>
            </div>
            {stuck.map((s) => (
              <div className="votedrow" key={s.rule.id}>
                <div className="votedwhat">
                  <b>{s.rule.title}</b>
                  <p className="fact">
                    The league passed this rule. The app does not enforce it yet.
                    {s.done.length > 0 && ` ${s.done.length} of ${s.done.length + s.todo.length} parts built.`}
                  </p>
                </div>
                <ul className="votedtasks">
                  {[...s.done, ...s.todo].map((t) => (
                    <li key={t.id} className={t.done ? 'done' : ''}>
                      <span className="tick" aria-hidden="true">{t.done ? '✓' : '☐'}</span>{t.label}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        )}

        <section className="card deadlines">
          <div className="cardhead">
            <h3>Deadlines</h3>
            {canEdit && <span className="pill amber mono">commissioner-writable</span>}
            {neverSet && <span className="mut">Never set for {SEASON}</span>}
            <div className="zoneseg" role="group" aria-label="Entering times in">
              <span className="mut">Entering times in</span>
              <div className="seg">
                {ZONES.map((z) => (
                  <button type="button" key={z.tz} className={zone === z.tz ? 'on' : ''}
                    disabled={!canEdit} aria-pressed={zone === z.tz} onClick={() => setZone(z.tz)}>
                    {z.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <table className="dltab">
            <thead>
              <tr>
                <th>Deadline</th><th>Set to</th><th>Status</th><th className="lk">Locked</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <DeadlineRowView key={r.key} r={r} field={FIELD[r.key]} canEdit={canEdit}
                  gate={GATE_OF[r.key]}
                  prefRule={r.key === 'preferences' ? prefRule : null}
                  onUseRule={() => prefRule.expected && setPrefDeadline(zonedInputValue(prefRule.expected, zone))}
                  onLockEarly={(on) => void lockEarly(GATE_OF[r.key]!, on)}
                  onAskUnlock={() => setUnlocking(GATE_OF[r.key]!)}
                  onRelock={() => void relock(GATE_OF[r.key]!)}
                />
              ))}

              {/*
                THE WAY IN, beside the date it belongs to. Free text: a Zoom link, a Meet link or a
                line of dial-in digits. Refusing anything that is not a URL would be the app
                deciding how the league meets.
              */}
              <tr className="dlrow nogate">
                <th scope="row">
                  <span className="dlname">Committee meeting — how to join</span>
                  <span className="dlwhy">URL or dial-in, not validated</span>
                </th>
                <td className="set">
                  <input id="cmlink" className="linkfield" type="text" inputMode="url"
                    placeholder="https://… or a dial-in number"
                    value={committeeLink} disabled={!canEdit}
                    onChange={(e) => setCommitteeLink(e.target.value)} />
                  <span className="echo">
                    {committeeLink.trim()
                      ? 'Shown on Home as the meeting approaches'
                      : 'Nothing set — Home will say no link has been posted'}
                  </span>
                </td>
                <td className="st" />
                <td className="lk"><span className="mut">—</span></td>
              </tr>

              {/* Read-only here: the lottery sets it, and two places to set one thing is two answers. */}
              <tr className="dlrow nogate">
                <th scope="row">
                  <span className="dlname">Draft order</span>
                  <span className="dlwhy">read-only here</span>
                </th>
                <td className="set">
                  <ol className="orderstrip">
                    {Array.from({ length: LEAGUE_2026.teams }, (_, i) => order[i]).map((g, i) => (
                      <li key={i} className={g ? '' : 'blank'}>
                        <span className="slotno">{i + 1}</span>
                        {g ? <GmBadge gm={g} /> : <b>—</b>}
                      </li>
                    ))}
                  </ol>
                  <span className="echo">Set by the lottery on Draft › Order and lottery</span>
                </td>
                <td className="st" />
                <td className="lk"><span className="mut">—</span></td>
              </tr>
            </tbody>
          </table>

          <div className="cardfoot dlfoot">
            <span className="mut">
              {saved ? 'Saved' : settings?.updatedAt
                ? `Last changed ${new Date(settings.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} by ${managerShort(settings.updatedBy ?? '')}`
                : 'Never set'}
              {' · GMs see every time in both zones · moving a date clears any exception'}
            </span>
            {canEdit && (
              <button type="button" className="btn primary" onClick={() => void save()}>Save</button>
            )}
          </div>
        </section>

        {/*
          THE FORCE-UNLOCK CONFIRMATION. Same dock and same sheet as Withdraw and the per-slate
          unlock on Declare, because it is the same class of act. It states the consequences in the
          present tense rather than asking whether he is sure, and it names the alternative for the
          one-man case so the wrong door is not taken for the right reason.
        */}
        {unlocking && (() => {
          const key = unlocking;
          const f = GATE_FIELDS[key];
          const when = (settings as unknown as Record<string, unknown> | null)?.[f.deadline] as string | null | undefined;
          const says = UNLOCK_SAYS[key];
          return dock(
            <>
              <button type="button" className="sheetscrim kdscrim" aria-label="Keep it locked"
                onClick={() => setUnlocking(null)} />
              <div className="kdsheet unlocksheet" role="dialog" aria-modal="true" aria-label={says.title}>
                <h2 className="kdsheeth">{says.title}</h2>
                <p className="kdsheetsub">
                  {when ? `The deadline passed ${inZone(when, LEAGUE_ZONE)} ET. ` : 'No deadline is set. '}
                  Unlocking reopens the gate for the whole league, not one person.
                </p>
                <ul className="unlocksays">
                  {says.lines.map((l) => <li key={l}>{l}</li>)}
                  <li>This is logged against your name: <b>{managerShort(gm)}</b>.</li>
                </ul>
                {key === 'keepers' && (
                  <p className="kdsheetsub">
                    A correction to one man&rsquo;s slate does not need this — that unlock lives on
                    his Declare screen.
                  </p>
                )}
                <div className="kdsheetacts">
                  <button type="button" className="kdsubmit"
                    onClick={() => { setUnlocking(null); void holdOpen(key); }}>
                    Unlock for everyone
                  </button>
                  <button type="button" className="kdkeep" onClick={() => setUnlocking(null)}>
                    Keep it locked
                  </button>
                </div>
              </div>
            </>,
          );
        })()}
      </Linkify>
    </div>
  );
}

/** One row. Four cells, and the fourth is where the three-state toggle lives. */
function DeadlineRowView(
  { r, field, canEdit, gate, prefRule, onUseRule, onLockEarly, onAskUnlock, onRelock }: {
    r: DeadlineRow;
    field: { value: string; set: (v: string) => void };
    canEdit: boolean;
    gate: GateKey | undefined;
    prefRule: ReturnType<typeof prefRuleCheck> | null;
    onUseRule: () => void;
    onLockEarly: (on: boolean) => void;
    onAskUnlock: () => void;
    onRelock: () => void;
  },
) {
  const passed = Boolean(r.gate?.passed);
  const held = r.heldOpen;
  /*
   * WHAT THE TOGGLE READS. On = shut, whether by hand or by the clock; off = open, whether by the
   * clock or by exception. Flipping it OFF once the date has passed is never a plain write.
   */
  const on = gate ? (held ? false : (r.locked || passed)) : false;
  const micro = !gate ? '' : held ? 'held open' : on ? 'locked' : 'locks at deadline';

  const flip = () => {
    if (!gate) return;
    if (held) { onRelock(); return; }
    if (on && passed && !r.locked) { onAskUnlock(); return; }
    onLockEarly(!on);
  };

  const dayOnly = (iso: string) => inZone(iso, LEAGUE_ZONE).replace(/,\s*\d{1,2}:\d{2}\s*[AP]M$/, '');

  return (
    <tr className={`dlrow${r.gates ? '' : ' nogate'}${held ? ' exception' : ''}`}>
      <th scope="row">
        <span className="dlname">{r.label}</span>
        {GATES_WHAT[r.key] && <span className="dlwhy">{GATES_WHAT[r.key]}</span>}
        {held && (
          <span className="dlwhy exc">
            unlocked by {managerShort(held.by)}, exception
            {r.iso && ` · deadline passed ${dayOnly(r.iso)}`}
          </span>
        )}
      </th>
      <td className="set">
        <input type="datetime-local" value={field.value} disabled={!canEdit}
          aria-label={`${r.label} — date and time`}
          onChange={(e) => field.set(e.target.value)} />
        <span className="echo">
          {r.iso
            ? ZONES.map((z) => `${clockIn(r.iso!, z.tz)} ${z.label}`).join(' / ')
            : (GATES_WHAT[r.key] ?? 'a date, not a gate')}
        </span>
        {prefRule && !prefRule.agrees && (
          <span className="rulehint">
            {prefRule.why}
            {canEdit && prefRule.expected && (
              <button type="button" className="linkbtn" onClick={onUseRule}>Use the rule&rsquo;s time</button>
            )}
          </span>
        )}
      </td>
      <td className="st">
        {r.gates && r.iso && (
          <>
            <span className={`pill mono ${held ? 'amber' : r.status === 'Open' ? (r.urgent ? 'amber' : 'green') : 'grey'}`}>
              {held ? 'Open · exception' : r.status}
            </span>
            <span className="stnote">{r.note}</span>
          </>
        )}
        {r.gates && !r.iso && <span className="stnote">{r.note}</span>}
      </td>
      <td className="lk">
        {gate ? (
          <span className="lockcell">
            {held ? (
              <button type="button" className="relock" disabled={!canEdit} onClick={onRelock}>
                Re-lock
              </button>
            ) : (
              <button type="button" role="switch" aria-checked={on} className={`toggle${on ? ' on' : ''}`}
                disabled={!canEdit} aria-label={`${r.label} lock`} onClick={flip}>
                <span className="knob" />
              </button>
            )}
            <span className="micro">{micro}</span>
          </span>
        ) : <span className="mut">—</span>}
      </td>
    </tr>
  );
}
